import { createContext, useCallback, useContext, useReducer, useRef } from 'react';
import * as briefStoriesManager from '../services/brief-stories';
import * as plausible from '../services/plausible';
import { Story } from '../services/models/story';
import { useServiceErrorHandler } from './useServiceErrorHandler';

// Brief stories are page-local server state (only BriefPage renders them),
// so they live in a local store that mirrors the retired nested Redux
// reducers: cursor pagination with merge-by-id, per-story tweet loading
// flags, and the clicked-state patch after the storyClick mutation.

interface BriefStoriesState {
  briefId?: number;
  stories?: Story[];
  hasMoreStories?: boolean;
  lastLoadedStoryCursor?: number;
  isFetching?: boolean;
}

type BriefStoriesAction =
  | { type: 'startFetching'; briefId: number }
  | { type: 'cancelFetching' }
  | {
      type: 'completePage';
      briefId: number;
      isFirstPage: boolean;
      stories: Story[];
      hasMoreStories: boolean;
      lastCursor: number;
    }
  | { type: 'patchStory'; storyId: number; patch: Partial<Story> };

const mergeStories = (existing: Story[], incoming: Story[]): Story[] => {
  const merged = existing.map(current => incoming.find(x => x.id === current.id) || current);
  incoming.forEach(story => {
    if (!existing.some(x => x.id === story.id)) {
      merged.push(story);
    }
  });
  return merged;
};

function briefStoriesReducer(state: BriefStoriesState, action: BriefStoriesAction) {
  switch (action.type) {
    case 'startFetching':
      // Changing briefs resets the accumulated list.
      if (state.briefId !== action.briefId) {
        return { briefId: action.briefId, isFetching: true } as BriefStoriesState;
      }
      return { ...state, isFetching: true };
    case 'cancelFetching':
      return { ...state, isFetching: false };
    case 'completePage':
      // A page that finished after the user switched briefs must not be
      // written under the new brief.
      if (state.briefId !== action.briefId) {
        return state;
      }
      return {
        ...state,
        isFetching: false,
        stories: action.isFirstPage
          ? [...action.stories]
          : mergeStories(state.stories || [], action.stories),
        hasMoreStories: action.hasMoreStories,
        lastLoadedStoryCursor: action.lastCursor,
      };
    case 'patchStory':
      return {
        ...state,
        stories: state.stories?.map(story =>
          story.id === action.storyId ? { ...story, ...action.patch } : story
        ),
      };
    default:
      return state;
  }
}

const PAGE_SIZE = 20;

export const useBriefStories = () => {
  const handleServiceError = useServiceErrorHandler();
  const [state, dispatch] = useReducer(briefStoriesReducer, {});
  // Serialize page loads within a brief the way the legacy isFetching guard
  // did, but let a brief switch supersede an in-flight request for another
  // brief (a single shared flag would silently drop the new brief's load and
  // leave it rendering the old brief's stories).
  const activeRequestRef = useRef<{ briefId: number; token: number } | null>(null);
  const requestTokenRef = useRef(0);

  const loadStoriesPage = useCallback(
    async (briefId: number, after: number) => {
      const active = activeRequestRef.current;
      if (active && active.briefId === briefId) {
        return;
      }
      const token = ++requestTokenRef.current;
      activeRequestRef.current = { briefId, token };
      dispatch({ type: 'startFetching', briefId });
      try {
        const page = await briefStoriesManager.getBriefStoriesPaged(briefId, after, PAGE_SIZE);
        if (requestTokenRef.current !== token) {
          return; // superseded by a newer request for a different brief
        }
        dispatch({
          type: 'completePage',
          briefId,
          isFirstPage: after === 0,
          stories: page.stories,
          hasMoreStories: page.hasMoreStories,
          lastCursor: page.lastCursor,
        });
      } catch (error) {
        if (requestTokenRef.current === token) {
          handleServiceError(error, () => dispatch({ type: 'cancelFetching' }));
        }
      } finally {
        if (activeRequestRef.current?.token === token) {
          activeRequestRef.current = null;
        }
      }
    },
    [handleServiceError]
  );

  const loadStoryTweets = useCallback(
    async (briefId: number, storyId: number) => {
      dispatch({ type: 'patchStory', storyId, patch: { isFetchingTweets: true } });
      try {
        const tweets = await briefStoriesManager.getStoryTweets(storyId);
        dispatch({
          type: 'patchStory',
          storyId,
          patch: { tweets, isFetchingTweets: false, tweetsLoaded: true },
        });
      } catch (error) {
        handleServiceError(error, () =>
          dispatch({
            type: 'patchStory',
            storyId,
            patch: { isFetchingTweets: false, tweetsLoaded: true },
          })
        );
      }
    },
    [handleServiceError]
  );

  const patchStory = useCallback((storyId: number, patch: Partial<Story>) => {
    dispatch({ type: 'patchStory', storyId, patch });
  }, []);

  return {
    stories: state.stories,
    hasMoreStories: state.hasMoreStories,
    lastLoadedStoryCursor: state.lastLoadedStoryCursor,
    isFetchingStories: state.isFetching,
    loadStoriesPage,
    loadStoryTweets,
    patchStory,
  };
};

// Wizard preview stories (draft-brief flow); state lives with the caller so
// the refine step and the edit sidebar share one preview.
export const useBriefStoriesPreview = () => {
  const handleServiceError = useServiceErrorHandler();
  // Same stale-response protection as loadStoriesPage: keyword edits can
  // fire overlapping refreshes, and an older response must not clobber a
  // newer preview.
  const previewTokenRef = useRef(0);
  const [preview, dispatchPreview] = useReducer(
    (
      state: { storiesPreview?: Story[]; isFetchingPreview: boolean },
      action: { type: 'start' } | { type: 'complete'; stories?: Story[] }
    ) => {
      switch (action.type) {
        case 'start':
          return { ...state, isFetchingPreview: true };
        case 'complete':
          return {
            storiesPreview: action.stories ?? state.storiesPreview,
            isFetchingPreview: false,
          };
        default:
          return state;
      }
    },
    { isFetchingPreview: false }
  );

  const refreshPreview = useCallback(
    async (briefId: number) => {
      const token = ++previewTokenRef.current;
      dispatchPreview({ type: 'start' });
      try {
        const stories = await briefStoriesManager.getBriefStoriesPreview(briefId);
        if (previewTokenRef.current === token) {
          dispatchPreview({ type: 'complete', stories });
        }
      } catch (error) {
        if (previewTokenRef.current === token) {
          handleServiceError(error, () => dispatchPreview({ type: 'complete' }));
        }
      }
    },
    [handleServiceError]
  );

  return { ...preview, refreshPreview };
};

// Lets deeply-nested story links report clicks back to whichever store owns
// the visible story list (brief page today, social feed later).
export const StoryClickContext = createContext<{
  onStoryViewed: (storyId: number) => void;
} | null>(null);

export const useMarkStoryAsViewed = () => {
  const handleServiceError = useServiceErrorHandler();
  const clickContext = useContext(StoryClickContext);

  return useCallback(
    async (storyId: number, briefId?: number) => {
      plausible.trackEvent('Story Clicked', { source: briefId ? 'brief' : 'social' });
      try {
        await briefStoriesManager.markStoryAsViewed(storyId);
        clickContext?.onStoryViewed(storyId);
      } catch (error) {
        handleServiceError(error);
      }
    },
    [clickContext, handleServiceError]
  );
};
